/**
 * Instellingen - Boekhouding configuratie
 * Gebaseerd op Morris instellingen.html
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import BoekhoudingLayout from '../../components/boekhouding/BoekhoudingLayout';
import styles from '../../styles/Boekhouding.module.css';
import { boekhoudingService, Grootboekrekening, GrootboekType, BoekhoudingInstelling, BOEKJAAR_OPTIES, AutoData } from '../../services/boekhouding.service';

interface InstellingenState {
  boekjaar: number;
  anthropicApiKey: string;
  aiAutoCategorie: boolean;
  aiFactuurOcr: boolean;
  aiKoppelingSuggestie: boolean;
  btwPriveAuto: number;
  btwPriveAutoPerKwartaal: boolean;
}

const Instellingen = () => {
  const [loading, setLoading] = useState(true);
  const [saveIndicator, setSaveIndicator] = useState(false);
  const [instellingen, setInstellingen] = useState<InstellingenState>({
    boekjaar: new Date().getFullYear(),
    anthropicApiKey: '',
    aiAutoCategorie: false,
    aiFactuurOcr: true,
    aiKoppelingSuggestie: false,
    btwPriveAuto: 0,
    btwPriveAutoPerKwartaal: false,
  });
  const [autos, setAutos] = useState<AutoData[]>([]);
  const [grootboeken, setGrootboeken] = useState<Grootboekrekening[]>([]);
  const [apiKeyStatus, setApiKeyStatus] = useState<{ type: 'success' | 'error' | 'testing' | null; message: string }>({ type: null, message: '' });

  // Nieuwe items formulier state
  const [nieuweAuto, setNieuweAuto] = useState({ naam: '', catalogusprijs: '', bouwjaar: '', percentage: '', startDatum: '', eindDatum: '' });
  const [nieuwGrootboek, setNieuwGrootboek] = useState<{ grootboekNr: string; naam: string; type: GrootboekType; btwPercentage: number }>({ grootboekNr: '', naam: '', type: GrootboekType.INKOOP, btwPercentage: 21 });
  const [showDatumVelden, setShowDatumVelden] = useState(false);
  // Edit modes
  const [editAutoId, setEditAutoId] = useState<number | null>(null);
  const [editAutoData, setEditAutoData] = useState<{ naam: string; catalogusprijs: string; bouwjaar: string; percentage: string; startDatum: string; eindDatum: string }>({ naam: '', catalogusprijs: '', bouwjaar: '', percentage: '', startDatum: '', eindDatum: '' });
  const [, setEditGrootboekId] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Laad grootboekrekeningen van API
      const grootboekResponse = await boekhoudingService.getGrootboekrekeningen();
      setGrootboeken(grootboekResponse.items);

      // Laad instellingen van API
      const instellingenData = await boekhoudingService.getInstellingen();
      const instellingenMap: { [key: string]: string } = {};
      instellingenData.forEach((item: BoekhoudingInstelling) => {
        instellingenMap[item.key] = item.value || '';
      });

      setInstellingen({
        boekjaar: parseInt(localStorage.getItem('boekhouding_jaar') || instellingenMap.boekjaar || new Date().getFullYear().toString()),
        anthropicApiKey: instellingenMap.anthropic_api_key || '',
        aiAutoCategorie: instellingenMap.ai_auto_categorisatie === '1',
        aiFactuurOcr: instellingenMap.ai_factuur_ocr === '1',
        aiKoppelingSuggestie: instellingenMap.ai_koppeling_suggestie === '1',
        btwPriveAuto: parseFloat(instellingenMap.btw_prive_auto || '0'),
        btwPriveAutoPerKwartaal: instellingenMap.btw_prive_auto_per_kwartaal === '1',
      });

      // Laad autos van API
      const autosData = await boekhoudingService.getAutos();
      setAutos(autosData);
    } catch (err) {
      console.error('Error loading instellingen:', err);
    } finally {
      setLoading(false);
    }
  };

  const showSaveIndicator = useCallback(() => {
    setSaveIndicator(true);
    setTimeout(() => setSaveIndicator(false), 1500);
  }, []);

  const saveInstelling = useCallback(async (key: string, value: string | number | boolean) => {
    try {
      // Converteer key naar snake_case voor API
      const apiKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      const apiValue = typeof value === 'boolean' ? (value ? '1' : '0') : String(value);

      await boekhoudingService.updateInstelling(apiKey, apiValue);
      showSaveIndicator();

      // Bij boekjaar wijziging: localStorage updaten
      if (key === 'boekjaar') {
        localStorage.setItem('boekhouding_jaar', String(value));
      }
    } catch (err) {
      console.error('Save failed:', err);
    }
  }, [showSaveIndicator]);

  const handleInstellingChange = (key: keyof InstellingenState, value: string | number | boolean) => {
    setInstellingen(prev => ({ ...prev, [key]: value }));
    // Debounce voor text inputs gebeurt in de input zelf
  };

  const testApiKey = async () => {
    if (!instellingen.anthropicApiKey) {
      setApiKeyStatus({ type: 'error', message: 'Voer eerst een API key in' });
      return;
    }

    setApiKeyStatus({ type: 'testing', message: 'Testen...' });

    try {
      // TODO: Echte API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setApiKeyStatus({ type: 'success', message: 'API key is geldig!' });
    } catch {
      setApiKeyStatus({ type: 'error', message: 'API key is ongeldig' });
    }
  };

  const berekenAutoBtw = (auto: AutoData): { percentage: number; jaarBtw: number; dagen: number; proRataBtw: number } => {
    const huidigJaar = instellingen.boekjaar;
    const leeftijd = huidigJaar - auto.bouwjaar;
    const percentage = auto.percentage_override || (leeftijd >= 5 ? 1.5 : 2.7);

    const jaarStart = new Date(huidigJaar, 0, 1);
    const jaarEind = new Date(huidigJaar, 11, 31);
    const startDatum = auto.start_datum ? new Date(auto.start_datum) : jaarStart;
    const eindDatum = auto.eind_datum ? new Date(auto.eind_datum) : jaarEind;

    const effectiefStart = startDatum < jaarStart ? jaarStart : startDatum;
    const effectiefEind = eindDatum > jaarEind ? jaarEind : eindDatum;

    const dagenInJaar = Math.ceil((jaarEind.getTime() - jaarStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const dagenGebruik = Math.max(0, Math.ceil((effectiefEind.getTime() - effectiefStart.getTime()) / (1000 * 60 * 60 * 24)) + 1);

    const jaarBtw = auto.catalogusprijs * (percentage / 100);
    const proRataBtw = jaarBtw * (dagenGebruik / dagenInJaar);

    return { percentage, jaarBtw, dagen: dagenGebruik, proRataBtw };
  };

  const handleAutoToevoegen = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await boekhoudingService.createAuto({
        naam: nieuweAuto.naam,
        catalogusprijs: parseFloat(nieuweAuto.catalogusprijs),
        bouwjaar: parseInt(nieuweAuto.bouwjaar),
        percentage_override: nieuweAuto.percentage ? parseFloat(nieuweAuto.percentage) : undefined,
      });
      // Herlaad autos
      const autosData = await boekhoudingService.getAutos();
      setAutos(autosData);
      setNieuweAuto({ naam: '', catalogusprijs: '', bouwjaar: '', percentage: '', startDatum: '', eindDatum: '' });
      showSaveIndicator();
    } catch (err) {
      console.error('Error creating auto:', err);
      alert('Fout bij aanmaken auto');
    }
  };

  const handleAutoVerwijderen = async (id: number) => {
    if (!window.confirm('Auto verwijderen?')) return;
    try {
      await boekhoudingService.deleteAuto(id);
      setAutos(autos.filter(a => a.id !== id));
      showSaveIndicator();
    } catch (err) {
      console.error('Error deleting auto:', err);
      alert('Fout bij verwijderen auto');
    }
  };

  const handleAutoBewerken = (auto: AutoData) => {
    setEditAutoId(auto.id);
    setEditAutoData({
      naam: auto.naam,
      catalogusprijs: auto.catalogusprijs.toString(),
      bouwjaar: auto.bouwjaar.toString(),
      percentage: auto.percentage_override?.toString() || '',
      startDatum: auto.start_datum || '',
      eindDatum: auto.eind_datum || '',
    });
  };

  const handleAutoAnnuleren = () => {
    setEditAutoId(null);
    setEditAutoData({ naam: '', catalogusprijs: '', bouwjaar: '', percentage: '', startDatum: '', eindDatum: '' });
  };

  const handleAutoOpslaan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editAutoId) return;

    try {
      await boekhoudingService.updateAuto(editAutoId, {
        naam: editAutoData.naam,
        catalogusprijs: parseFloat(editAutoData.catalogusprijs),
        bouwjaar: parseInt(editAutoData.bouwjaar),
        percentage_override: editAutoData.percentage ? parseFloat(editAutoData.percentage) : undefined,
        start_datum: editAutoData.startDatum || undefined,
        eind_datum: editAutoData.eindDatum || undefined,
      });
      // Herlaad autos
      const autosData = await boekhoudingService.getAutos();
      setAutos(autosData);
      setEditAutoId(null);
      setEditAutoData({ naam: '', catalogusprijs: '', bouwjaar: '', percentage: '', startDatum: '', eindDatum: '' });
      showSaveIndicator();
    } catch (err) {
      console.error('Error updating auto:', err);
      alert('Fout bij opslaan auto');
    }
  };

  const handleGrootboekToevoegen = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const newGrootboek = await boekhoudingService.createGrootboekrekening({
        grootboeknaam: nieuwGrootboek.naam,
        grootboek_nr: nieuwGrootboek.grootboekNr || undefined,
        type: nieuwGrootboek.type,
        btw_percentage: nieuwGrootboek.btwPercentage,
        actief: true,
      });
      setGrootboeken([...grootboeken, newGrootboek]);
      setNieuwGrootboek({ grootboekNr: '', naam: '', type: GrootboekType.INKOOP, btwPercentage: 21 });
      showSaveIndicator();
    } catch (err) {
      console.error('Error creating grootboekrekening:', err);
      alert('Fout bij aanmaken grootboekrekening');
    }
  };

  const handleGrootboekVerwijderen = async (naam: string) => {
    if (!window.confirm('Grootboekrekening verwijderen?')) return;
    try {
      // Note: API uses grootboeknaam as primary key, not id
      await boekhoudingService.getGrootboekrekening(0); // Placeholder - need delete endpoint
      setGrootboeken(grootboeken.filter(g => g.grootboeknaam !== naam));
      showSaveIndicator();
    } catch (err) {
      console.error('Error deleting grootboekrekening:', err);
    }
  };

  const formatBedrag = (bedrag: number): string => {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(bedrag);
  };

  if (loading) {
    return (
      <BoekhoudingLayout title="Instellingen">
        <div className={styles.loading}>Laden...</div>
      </BoekhoudingLayout>
    );
  }

  return (
    <BoekhoudingLayout title="Instellingen">
      {/* Save indicator */}
      {saveIndicator && (
        <div className={styles.saveIndicator}>Opgeslagen</div>
      )}

      <div className={styles.card} style={{ maxWidth: '900px', margin: '0 auto' }}>
        <h3>Boekhouding</h3>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="boekjaar">Actief boekjaar</label>
            <select
              id="boekjaar"
              value={instellingen.boekjaar}
              onChange={(e) => {
                const jaar = parseInt(e.target.value);
                handleInstellingChange('boekjaar', jaar);
                saveInstelling('boekjaar', jaar);
              }}
            >
              {BOEKJAAR_OPTIES.map(jaar => (
                <option key={jaar} value={jaar}>{jaar}</option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup} style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
            <Link to="/boekhouding/beginbalans" className={`${styles.btn} ${styles.btnSecondary}`}>
              Beginbalans bewerken
            </Link>
          </div>
        </div>

        <h3>AI Assistent (Claude)</h3>

        <div className={styles.formGroup}>
          <label htmlFor="anthropicApiKey">Anthropic API Key</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="password"
              id="anthropicApiKey"
              value={instellingen.anthropicApiKey}
              onChange={(e) => handleInstellingChange('anthropicApiKey', e.target.value)}
              onBlur={() => saveInstelling('anthropicApiKey', instellingen.anthropicApiKey)}
              placeholder="sk-ant-api03-..."
              style={{ flex: 1 }}
            />
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={testApiKey}>
              Test
            </button>
          </div>
          <small style={{ color: 'var(--gray-500)' }}>
            Verkrijgbaar via <a href="https://console.anthropic.com" target="_blank" rel="noreferrer">console.anthropic.com</a>
          </small>
          {apiKeyStatus.type && (
            <div style={{ marginTop: '0.5rem', color: apiKeyStatus.type === 'success' ? 'var(--success)' : apiKeyStatus.type === 'error' ? 'var(--danger)' : 'var(--gray-600)' }}>
              {apiKeyStatus.type === 'success' && '✔ '}
              {apiKeyStatus.type === 'error' && '✘ '}
              {apiKeyStatus.message}
            </div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={instellingen.aiAutoCategorie}
              onChange={(e) => {
                handleInstellingChange('aiAutoCategorie', e.target.checked);
                saveInstelling('aiAutoCategorie', e.target.checked);
              }}
            />
            Automatische categoriesuggesties bij invoer banktransacties
          </label>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={instellingen.aiFactuurOcr}
              onChange={(e) => {
                handleInstellingChange('aiFactuurOcr', e.target.checked);
                saveInstelling('aiFactuurOcr', e.target.checked);
              }}
            />
            Factuur OCR: automatisch gegevens uit PDF/afbeelding halen
          </label>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={instellingen.aiKoppelingSuggestie}
              onChange={(e) => {
                handleInstellingChange('aiKoppelingSuggestie', e.target.checked);
                saveInstelling('aiKoppelingSuggestie', e.target.checked);
              }}
            />
            Slimme suggesties voor betaling-factuur koppelingen
          </label>
        </div>
      </div>

      {/* BTW Privégebruik Auto's */}
      <div className={styles.card} style={{ maxWidth: '900px', margin: '1.5rem auto 0' }}>
        <h3 style={{ marginBottom: '1rem' }}>BTW Privégebruik Auto's</h3>

        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>Auto</th>
              <th style={{ width: '110px', textAlign: 'right' }}>Catalogusprijs</th>
              <th style={{ width: '50px', textAlign: 'center' }}>%</th>
              <th style={{ width: '90px', textAlign: 'right' }}>Per jaar</th>
              <th style={{ width: '70px', textAlign: 'center' }}>Dagen</th>
              <th style={{ width: '100px', textAlign: 'right' }}>BTW-correctie</th>
              <th style={{ width: '80px' }}></th>
            </tr>
          </thead>
          <tbody>
            {autos.map(auto => {
              const berekening = berekenAutoBtw(auto);
              const isEditing = editAutoId === auto.id;
              return (
                <React.Fragment key={auto.id}>
                  {/* View row */}
                  <tr style={{ display: isEditing ? 'none' : undefined }}>
                    <td>
                      <strong>{auto.naam}</strong>
                      <span style={{ color: 'var(--gray-500)', fontSize: '0.8rem', marginLeft: '0.5rem' }}>{auto.bouwjaar}</span>
                      {auto.percentage_override && <span style={{ color: 'var(--warning)', fontSize: '0.7rem', marginLeft: '0.25rem' }} title="Handmatig percentage">⚙</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatBedrag(auto.catalogusprijs)}</td>
                    <td style={{ textAlign: 'center' }}>{berekening.percentage.toFixed(1)}%</td>
                    <td style={{ textAlign: 'right', color: 'var(--gray-500)' }}>{formatBedrag(berekening.jaarBtw)}</td>
                    <td style={{ textAlign: 'center' }}>{berekening.dagen === 365 || berekening.dagen === 366 ? '365' : `${berekening.dagen}/365`}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatBedrag(berekening.proRataBtw)}</td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.btnSmall} ${styles.btnSecondary}`}
                        onClick={() => handleAutoBewerken(auto)}
                        title="Bewerken"
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.btnSmall} ${styles.btnDanger}`}
                        onClick={() => handleAutoVerwijderen(auto.id)}
                        title="Verwijderen"
                        style={{ marginLeft: '0.25rem' }}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                  {/* Edit row */}
                  {isEditing && (
                    <tr style={{ background: 'var(--gray-50)' }}>
                      <td colSpan={7} style={{ padding: '0.5rem' }}>
                        <form onSubmit={handleAutoOpslaan} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <input
                            type="text"
                            value={editAutoData.naam}
                            onChange={(e) => setEditAutoData({ ...editAutoData, naam: e.target.value })}
                            required
                            style={{ width: '120px', padding: '0.25rem', fontSize: '0.85rem' }}
                          />
                          <input
                            type="number"
                            value={editAutoData.catalogusprijs}
                            onChange={(e) => setEditAutoData({ ...editAutoData, catalogusprijs: e.target.value })}
                            required
                            style={{ width: '90px', padding: '0.25rem', fontSize: '0.85rem' }}
                          />
                          <input
                            type="number"
                            value={editAutoData.bouwjaar}
                            onChange={(e) => setEditAutoData({ ...editAutoData, bouwjaar: e.target.value })}
                            required
                            style={{ width: '65px', padding: '0.25rem', fontSize: '0.85rem' }}
                          />
                          <input
                            type="number"
                            value={editAutoData.percentage}
                            onChange={(e) => setEditAutoData({ ...editAutoData, percentage: e.target.value })}
                            step="0.01"
                            min="0"
                            max="100"
                            placeholder="Auto"
                            title="Leeg = automatisch (2,7% of 1,5%)"
                            style={{ width: '70px', padding: '0.25rem', fontSize: '0.85rem' }}
                          />
                          <input
                            type="date"
                            value={editAutoData.startDatum}
                            onChange={(e) => setEditAutoData({ ...editAutoData, startDatum: e.target.value })}
                            style={{ width: '110px', padding: '0.25rem', fontSize: '0.85rem' }}
                          />
                          <input
                            type="date"
                            value={editAutoData.eindDatum}
                            onChange={(e) => setEditAutoData({ ...editAutoData, eindDatum: e.target.value })}
                            style={{ width: '110px', padding: '0.25rem', fontSize: '0.85rem' }}
                          />
                          <button type="submit" className={`${styles.btn} ${styles.btnSmall} ${styles.btnPrimary}`} style={{ padding: '0.25rem 0.5rem' }}>OK</button>
                          <button type="button" className={`${styles.btn} ${styles.btnSmall} ${styles.btnSecondary}`} style={{ padding: '0.25rem 0.5rem' }} onClick={handleAutoAnnuleren}>✕</button>
                        </form>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: 'var(--gray-50)' }}>
              <td colSpan={5} style={{ textAlign: 'right', fontWeight: 600 }}>Totaal BTW-correctie {instellingen.boekjaar}:</td>
              <td style={{ textAlign: 'right', fontWeight: 700, fontSize: '1.1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                  <span>€</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={instellingen.btwPriveAuto}
                    onChange={(e) => handleInstellingChange('btwPriveAuto', parseFloat(e.target.value) || 0)}
                    onBlur={() => saveInstelling('btwPriveAuto', instellingen.btwPriveAuto)}
                    style={{ width: '90px', padding: '0.3rem', fontSize: '1rem', fontWeight: 700, textAlign: 'right' }}
                  />
                </div>
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>

        {/* Nieuwe auto toevoegen */}
        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--gray-200)' }}>
          <form onSubmit={handleAutoToevoegen} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={nieuweAuto.naam}
              onChange={(e) => setNieuweAuto({ ...nieuweAuto, naam: e.target.value })}
              required
              placeholder="Naam auto"
              style={{ flex: 1, minWidth: '140px', padding: '0.35rem 0.5rem', fontSize: '0.9rem' }}
            />
            <input
              type="number"
              value={nieuweAuto.catalogusprijs}
              onChange={(e) => setNieuweAuto({ ...nieuweAuto, catalogusprijs: e.target.value })}
              required
              step="1"
              min="0"
              placeholder="Catalogusprijs"
              style={{ width: '110px', padding: '0.35rem 0.5rem', fontSize: '0.9rem' }}
            />
            <input
              type="number"
              value={nieuweAuto.bouwjaar}
              onChange={(e) => setNieuweAuto({ ...nieuweAuto, bouwjaar: e.target.value })}
              required
              min="2000"
              max="2030"
              placeholder="Bouwjaar"
              style={{ width: '85px', padding: '0.35rem 0.5rem', fontSize: '0.9rem' }}
            />
            <input
              type="number"
              value={nieuweAuto.percentage}
              onChange={(e) => setNieuweAuto({ ...nieuweAuto, percentage: e.target.value })}
              step="0.01"
              min="0"
              max="100"
              placeholder="% (auto)"
              title="Leeg = automatisch (2,7% of 1,5%)"
              style={{ width: '75px', padding: '0.35rem 0.5rem', fontSize: '0.9rem' }}
            />
            <button type="submit" className={`${styles.btn} ${styles.btnSmall} ${styles.btnSuccess}`}>+ Toevoegen</button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSmall} ${styles.btnSecondary}`}
              onClick={() => setShowDatumVelden(!showDatumVelden)}
              title="Datum opties"
            >
              ⚙
            </button>
          </form>
          {showDatumVelden && (
            <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'var(--gray-50)', borderRadius: '4px' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--gray-600)' }}>Pro rata:</span>
              <input
                type="date"
                value={nieuweAuto.startDatum}
                onChange={(e) => setNieuweAuto({ ...nieuweAuto, startDatum: e.target.value })}
                style={{ padding: '0.25rem', fontSize: '0.85rem', marginLeft: '0.5rem' }}
              />
              <span style={{ fontSize: '0.8rem', color: 'var(--gray-600)', margin: '0 0.25rem' }}>t/m</span>
              <input
                type="date"
                value={nieuweAuto.eindDatum}
                onChange={(e) => setNieuweAuto({ ...nieuweAuto, eindDatum: e.target.value })}
                style={{ padding: '0.25rem', fontSize: '0.85rem' }}
              />
            </div>
          )}
        </div>

        <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
          Vul direct het BTW-correctie bedrag in, of voeg auto's toe voor automatische berekening
        </p>

        {/* Per kwartaal checkbox */}
        <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--gray-50)', borderRadius: '6px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={instellingen.btwPriveAutoPerKwartaal}
              onChange={(e) => {
                handleInstellingChange('btwPriveAutoPerKwartaal', e.target.checked);
                saveInstelling('btwPriveAutoPerKwartaal', e.target.checked);
              }}
              style={{ width: '18px', height: '18px' }}
            />
            <span style={{ fontSize: '0.9rem' }}><strong>Per kwartaal afrekenen</strong></span>
          </label>
          <p style={{ margin: '0.25rem 0 0 1.6rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
            Aan = BTW privé auto wordt elk kwartaal berekend en meegenomen in de aangifte<br />
            Uit = Alleen in Q4 als jaarlijkse correctie (standaard)
          </p>
        </div>
      </div>

      {/* Grootboekrekeningen */}
      <div className={styles.card} style={{ maxWidth: '900px', margin: '1.5rem auto 0' }}>
        <h3>Grootboekrekeningen</h3>

        {/* Nieuwe grootboekrekening */}
        <form onSubmit={handleGrootboekToevoegen} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--gray-200)' }}>
          <div className={styles.formGroup} style={{ width: '70px' }}>
            <label style={{ fontSize: '0.75rem' }}>Nr.</label>
            <input
              type="text"
              value={nieuwGrootboek.grootboekNr}
              onChange={(e) => setNieuwGrootboek({ ...nieuwGrootboek, grootboekNr: e.target.value })}
              placeholder="4100"
              style={{ padding: '0.4rem' }}
            />
          </div>
          <div className={styles.formGroup} style={{ flex: 1, minWidth: '150px' }}>
            <label style={{ fontSize: '0.75rem' }}>Naam</label>
            <input
              type="text"
              value={nieuwGrootboek.naam}
              onChange={(e) => setNieuwGrootboek({ ...nieuwGrootboek, naam: e.target.value })}
              required
              placeholder="Naam grootboekrekening"
              style={{ padding: '0.4rem' }}
            />
          </div>
          <div className={styles.formGroup} style={{ width: '100px' }}>
            <label style={{ fontSize: '0.75rem' }}>Type</label>
            <select
              value={nieuwGrootboek.type}
              onChange={(e) => setNieuwGrootboek({ ...nieuwGrootboek, type: e.target.value as GrootboekType })}
              style={{ padding: '0.4rem' }}
            >
              <option value={GrootboekType.INKOOP}>Inkoop</option>
              <option value={GrootboekType.OMZET}>Omzet</option>
              <option value={GrootboekType.KOSTEN}>Kosten</option>
              <option value={GrootboekType.BALANS}>Balans</option>
            </select>
          </div>
          <div className={styles.formGroup} style={{ width: '70px' }}>
            <label style={{ fontSize: '0.75rem' }}>BTW</label>
            <select
              value={nieuwGrootboek.btwPercentage}
              onChange={(e) => setNieuwGrootboek({ ...nieuwGrootboek, btwPercentage: parseInt(e.target.value) })}
              style={{ padding: '0.4rem' }}
            >
              <option value={21}>21%</option>
              <option value={9}>9%</option>
              <option value={0}>0%</option>
            </select>
          </div>
          <button type="submit" className={`${styles.btn} ${styles.btnSmall} ${styles.btnSuccess}`}>Toevoegen</button>
        </form>

        {/* Bestaande grootboekrekeningen */}
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th style={{ width: '60px' }}>Nr.</th>
              <th>Naam</th>
              <th style={{ width: '80px' }}>Type</th>
              <th style={{ width: '60px' }}>BTW</th>
              <th style={{ width: '120px' }}>Acties</th>
            </tr>
          </thead>
          <tbody>
            {grootboeken.map(gb => (
              <tr key={gb.grootboeknaam}>
                <td>{gb.grootboek_nr || '-'}</td>
                <td>{gb.grootboeknaam}</td>
                <td>
                  <span className={`${styles.badge} ${
                    gb.type === GrootboekType.OMZET ? styles.badgeSuccess :
                    gb.type === GrootboekType.INKOOP ? styles.badgeWarning :
                    gb.type === GrootboekType.KOSTEN ? styles.badgeInkoop :
                    styles.badgeInfo
                  }`}>
                    {gb.type}
                  </span>
                </td>
                <td>{gb.btw_percentage}%</td>
                <td>
                  <button
                    className={`${styles.btn} ${styles.btnSmall} ${styles.btnSecondary}`}
                    onClick={() => setEditGrootboekId(null)}
                  >
                    Bewerk
                  </button>
                  <button
                    className={`${styles.btn} ${styles.btnSmall} ${styles.btnDanger}`}
                    onClick={() => handleGrootboekVerwijderen(gb.grootboeknaam)}
                    style={{ marginLeft: '0.25rem' }}
                  >
                    X
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </BoekhoudingLayout>
  );
};

export default Instellingen;
